import * as React from 'react';
import * as R from 'remeda';
import { DocumentsService } from 'services/documents';
import { UsersService } from 'services/users';
import { Container, Flex, Box, Grid } from 'components/layout';
import { Text } from 'components/typography';
import { breakpoints } from 'utils/breakpoints';
import { InlineLoadingIndicator } from 'components/LoadingIndicator';
import { isQueryLoading } from 'utils/functions/is-query-loading';
import { useIntl } from 'lib/intl';
import { ReliningSchemaFinal } from 'global/types/templates';
import { ProjectCard } from 'components/cards/ProjectCard/ProjectCard';
import * as Icons from 'components/icons';

const ITEMS_PER_PAGE = 9; // 3x3 grid

export const Projects: React.FC = () => {
  const intl = useIntl();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchQuery, setSearchQuery] = React.useState('');
  const { allUsers } = UsersService.useGetUsers();

  const { data, isLoading, isFetching } = DocumentsService.usePaginatedDocuments(
    undefined,
    false,
    'dateUpdated',
    'DESC',
  );

  const documents = React.useMemo(
    () => R.flatMap(data?.pages ?? [], (page) => page.documents),
    [data?.pages],
  );

  // Filter only relining documents
  const reliningDocuments = documents.filter(
    (doc) => doc.templateId === 'relining',
  );

  // Filter by search query
  const filteredDocuments = React.useMemo(() => {
    if (!searchQuery.trim()) return reliningDocuments;
    const query = searchQuery.toLowerCase();
    return reliningDocuments.filter((doc) => {
      const documentData = doc.documentData as ReliningSchemaFinal | undefined;
      const projectName = documentData?.metadata?.projectNameAndNumber || '';
      return projectName.toLowerCase().includes(query);
    });
  }, [reliningDocuments, searchQuery]);

  // Reset page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedDocuments = filteredDocuments.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const getUserName = (userId?: string) => {
    if (!userId) return '-';
    const userEntry = allUsers.find((u) => u.userId === userId);
    if (!userEntry?.user) return '-';
    return `${userEntry.user.firstName} ${userEntry.user.lastName}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  if (isQueryLoading(isLoading, data)) {
    return <InlineLoadingIndicator />;
  }

  const hasDocuments = filteredDocuments.length > 0;

  return (
    <Flex flexDirection="column">
      <Container
        variant="spacer"
        sx={{ px: 2, py: 4, flexGrow: 1, display: 'flex' }}
      >
        <Flex
          flexDirection="column"
          sx={{
            width: '100%',
            gap: 1,
          }}
        >
          <Flex
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              mt: 2,
              gap: 3,
            }}
          >
            <Text
              as="h1"
              sx={{
                fontSize: '4xl',
                fontWeight: 500,
                color: '#111827',
              }}
            >
              Active Projects
            </Text>
            <Flex
              sx={{
                alignItems: 'center',
                border: '1px solid #e5e7eb',
                borderRadius: 9999,
                px: 3,
                py: 2,
                gap: 2,
                width: 'auto',
              }}
            >
              <Icons.Search width={16} stroke="#6b7280" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#111827',
                  width: '100%',
                }}
              />
            </Flex>
          </Flex>
          <Text
            sx={{
              fontSize: 'md',
              color: '#6b7280',
              mt: -2,
            }}
          >
            Manage and track your ongoing projects.
          </Text>

          {!hasDocuments && !isFetching && (
            <Text sx={{ color: 'grey.400', textAlign: 'center' }}>
              {searchQuery
                ? 'No projects found matching your search.'
                : intl.formatMessage({ id: 'documents.noDocuments' })}
            </Text>
          )}

          <Grid
            sx={{
              gridTemplateColumns: breakpoints({
                _: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              }),
              gap: 4,
              mt: 8,
            }}
          >
            {paginatedDocuments.map((document) => {
              const documentData = document.documentData as
                | ReliningSchemaFinal
                | undefined;
              const signedByUserId = document.completedById
                || document.assigneeId
                || document.creatorId;

              return (
                <ProjectCard
                  key={document.id}
                  projectName={
                    documentData?.metadata?.projectNameAndNumber || '-'
                  }
                  signedBy={getUserName(signedByUserId)}
                  date={formatDate(document.dateCreated)}
                />
              );
            })}
          </Grid>

          {totalPages > 1 && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 8,
                mb: 8,
              }}
            >
              <Flex
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 9,
                }}
              >
                <Box
                  as="button"
                  onClick={() => currentPage > 1 && goToPage(currentPage - 1)}
                  sx={{
                    px: 3,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'sm',
                    fontWeight: 500,
                    backgroundColor: '#e5e7eb',
                    color: '#6c7180',
                    border: 'none',
                    borderRadius: 6,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor:
                        currentPage === 1 ? '#e5e7eb' : '#d1d5db',
                    },
                  }}
                >
                  Previous
                </Box>
                <Flex
                  sx={{
                    gap: 2,
                    alignItems: 'center',
                    flexShrink: 0,
                    flexGrow: 0,
                    width: 'auto',
                  }}
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <Box
                        key={page}
                        as="button"
                        onClick={() => goToPage(page)}
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 6,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'sm',
                          backgroundColor:
                            currentPage === page ? 'primary.500' : '#e5e7eb',
                          color: currentPage === page ? '#fff' : '#6c7180',
                          fontWeight: currentPage === page ? 600 : 500,
                          '&:hover': {
                            backgroundColor:
                              currentPage === page ? 'primary.600' : '#d1d5db',
                          },
                        }}
                      >
                        {page}
                      </Box>
                    ),
                  )}
                </Flex>
                <Box
                  as="button"
                  onClick={() =>
                    currentPage < totalPages && goToPage(currentPage + 1)}
                  sx={{
                    px: 3,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'sm',
                    fontWeight: 500,
                    backgroundColor: '#e5e7eb',
                    color: '#6c7180',
                    border: 'none',
                    borderRadius: 6,
                    cursor:
                      currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor:
                        currentPage === totalPages ? '#e5e7eb' : '#d1d5db',
                    },
                  }}
                >
                  Next
                </Box>
              </Flex>
            </Box>
          )}
        </Flex>
      </Container>
    </Flex>
  );
};
